// =============================================================================
// notificationsPlan.js — the PURE half of "make reorder alerts real".
//
// Given a kitchen tap, this decides what (if anything) should be recorded and
// with what arguments. No network, no browser, no Supabase import — so the rules
// that matter can be tested hermetically, the way the wastage shaping is.
// notificationsSync.js is the thin thing that actually makes the call.
//
// Nothing in here can send an email. There is no sender. It decides between
// "raise an alert", "move one along", and "do nothing".
// =============================================================================

// ---- which kitchen actions mean what ----------------------------------------
// Raising an alert. These two are what the whole thing exists for.
export const FLAG_FOR = {
  stockRunningLow: "running_low",
  stockOutOfStock: "out_of_stock",
};

// Moving an existing alert along. `stockDelivered` means it physically turned
// up; `backInStock` means the storeroom flag has cleared, which is the moment
// the job is genuinely done. Both already fire today — we are only letting them
// land somewhere. Without this half the outbox only ever grows, and a list that
// only grows stops being read by the second week.
export const STEP_FOR = {
  stockDelivered: "delivered",
  backInStock: "resolved",
};

// ---- one confirmed tap = one token ------------------------------------------
// The database already refuses to tell anybody about the same item and flag
// twice in a London day (the throttle, migration 0044). This answers the
// different question: is this the same TAP I have already recorded?
//
// It matters because a suppressed alert is deliberately KEPT — "we chose to say
// nothing, and here is why" — so without a token a double-tap on the confirm
// button, a re-render firing the handler twice, or a retried fire would each
// leave another suppressed row behind. Three rows, one event, and a queue that
// slowly stops meaning anything.
//
// So: two fires of the same (shop, action, item) inside a short window are one
// tap arriving twice — they reuse the token, and the second is a no-op
// server-side. A GENUINE re-flag later in the day gets a fresh token and IS
// recorded (suppressed, with its reason) rather than vanishing. That is the
// distinction between "key it per day" — which loses real repeats — and "key it
// per confirmation".
export const SAME_TAP_WINDOW_MS = 90_000;

const _tapTokens = new Map();

// Deliberately date-free and PIN-free. This module is never handed the staff
// PIN in the first place, which is the guarantee that actually holds.
function nonce() {
  return Math.random().toString(36).slice(2, 10) + "-" + Date.now().toString(36);
}

export function tokenForTap(siteKey, action, item, now = Date.now()) {
  const key = [siteKey, action, item].map((p) => String(p ?? "").trim()).join("|").slice(0, 160);
  // Keep the map small — a kiosk tablet stays open all day.
  for (const [k, v] of _tapTokens) {
    if (now - v.at >= SAME_TAP_WINDOW_MS) _tapTokens.delete(k);
  }
  const seen = _tapTokens.get(key);
  // `at` is deliberately NOT refreshed on a repeat: a burst of taps collapses to
  // one token, but the window still closes 90s after the FIRST one, so a real
  // re-flag ten minutes later is a new event and gets recorded.
  if (seen && now - seen.at < SAME_TAP_WINDOW_MS) return seen.token;
  const token = key + "|" + nonce();
  _tapTokens.set(key, { token, at: now });
  return token;
}

// Test seam — lets a test start from a clean slate.
export function _resetTapTokens() { _tapTokens.clear(); }

// ---- the decision -----------------------------------------------------------
// Returns one of:
//   { skip: "<plain-English reason>" }                  — do nothing
//   { rpc: "notification_flag_kiosk",     args: {...} } — raise an alert
//   { rpc: "notification_progress_kiosk", args: {...} } — move / close one
//
// No staff id is ever in `args`: the database takes it from the ~15-minute pass
// the PIN just minted, which is what makes "who noticed" truthful rather than
// something a tablet asserted.
export function planReorderSignal({ siteKey, item, action, pass = null, now = Date.now() } = {}) {
  const flag = FLAG_FOR[action];
  const step = STEP_FOR[action];
  if (!flag && !step) return { skip: "not an alerting action" };

  const cleanItem = String(item ?? "").trim();
  if (!cleanItem) return { skip: "no item" };
  if (!siteKey) return { skip: "no shop" };
  // ensurePass returns {ok:true} / {ok:true, skipped:true} / {ok:false}. Without
  // a pass the database refuses us anyway ('no_pass'), so don't bother it.
  if (pass && pass.ok === false) return { skip: "no staff pass" };

  if (flag) {
    return {
      rpc: "notification_flag_kiosk",
      args: {
        p_site: siteKey,
        p_item: cleanItem,
        p_flag: flag,
        p_idem: tokenForTap(siteKey, action, cleanItem, now),
      },
    };
  }

  // Closing the loop needs no token: the underlying functions are forward-only
  // and find nothing open the second time — proven on staging, not assumed.
  return {
    rpc: "notification_progress_kiosk",
    args: { p_site: siteKey, p_item: cleanItem, p_step: step, p_note: null },
  };
}

// The answers notification_flag_kiosk gives that mean "recorded, all good".
// 'duplicate' counts: it means the tap was already recorded, which is a success.
export const GOOD_FLAG_RESULTS = new Set(["queued", "suppressed", "duplicate"]);

// ---- the urgent-incident decision -------------------------------------------
// The reorder twin above answers "should this kitchen tap raise/close a stock
// alert?". This answers the narrower FOH question: an incident was just filed —
// does it warrant paging the managers now? Only an URGENT one does; a non-urgent
// incident is logged for review and never pages anyone.
//
// The idempotency token is handed IN, not minted here (the sync layer reuses a
// same-tap token so a double submit is ONE page), which keeps this pure and lets
// a test assert `args` verbatim. No staff id in args — the database takes "who
// filed it" from the app_session pass, never from the tablet.
export function planIncidentSignal({ siteKey, type, severity, summary, token } = {}) {
  if (!siteKey) return { skip: "no shop" };
  const sev = String(severity ?? "").trim().toLowerCase();
  if (sev !== "urgent") return { skip: "not urgent" };        // only urgent pages
  const cleanType = String(type ?? "").trim();
  if (!cleanType) return { skip: "no type" };
  if (!token) return { skip: "no token" };                    // unkeyed = doubles on a re-fire

  return {
    rpc: "notification_incident_kiosk",
    args: {
      p_site: siteKey,
      p_type: cleanType,
      p_severity: sev,
      p_summary: String(summary ?? "").trim().slice(0, 140) || null,
      p_idem: token,
    },
  };
}
